/**
 * Picks a random subset of collection product cards from a server-rendered stash.
 * Uses Math.random() on each page load so cached HTML still gets fresh picks.
 */

/**
 * Fisher–Yates shuffle (returns a new array).
 * @template T
 * @param {T[]} items
 * @returns {T[]}
 */
function shuffle(items) {
  const shuffled = [...items];

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled;
}

/**
 * @param {HTMLElement} sectionRoot
 * @returns {string[]}
 */
function readEmbeddedHandles(sectionRoot) {
  const poolElement = sectionRoot.querySelector('[data-randomize-handles]');
  if (!(poolElement instanceof HTMLScriptElement)) return [];

  try {
    const pool = JSON.parse(poolElement.textContent || '[]');
    return Array.isArray(pool) ? pool.filter((handle) => typeof handle === 'string' && handle.length) : [];
  } catch {
    return [];
  }
}

/**
 * @param {HTMLElement} stash
 * @returns {Map<string, HTMLElement>}
 */
function buildStashMap(stash) {
  /** @type {Map<string, HTMLElement>} */
  const stashMap = new Map();

  stash.querySelectorAll('[data-randomize-stash-item]').forEach((item) => {
    if (!(item instanceof HTMLElement)) return;
    if (!item.querySelector('product-card')) return;

    const handle = item.dataset.productHandle;
    if (!handle || stashMap.has(handle)) return;

    stashMap.set(handle, item);
  });

  return stashMap;
}

/**
 * Resets entrance animation state so cloned cards can animate in the grid.
 * @param {ParentNode} root
 */
function resetEntranceState(root) {
  root.querySelectorAll('product-card[data-product-card-entrance]').forEach((card) => {
    if (!(card instanceof HTMLElement)) return;
    card.classList.remove('product-card--entrance-visible');
    card.classList.add('product-card--entrance-pending');
  });

  root.querySelectorAll('product-card[data-product-card-swatches-entrance]').forEach((card) => {
    if (!(card instanceof HTMLElement)) return;
    delete card.dataset.swatchesEntranceBound;
    delete card.dataset.swatchesEntranceBindingStarted;

    card
      .querySelectorAll('li.variant-option__swatch, label.variant-option__button-label--has-swatch')
      .forEach((element) => {
        element.classList.remove('variant-option__swatch--entrance-visible');
        element.classList.remove('variant-option__swatch--entrance-pending');
      });

    const inner = card.querySelector('product-swatches') || card.querySelector('variant-picker');
    if (inner instanceof HTMLElement) {
      delete inner.dataset.swatchesEntranceReduced;
    }
  });
}

/**
 * @param {HTMLElement} stashItem
 * @returns {HTMLElement}
 */
function cloneStashItem(stashItem) {
  const clone = stashItem.cloneNode(true);
  clone.removeAttribute('data-randomize-stash-item');
  clone.hidden = false;
  clone.querySelectorAll('[id]').forEach((element) => {
    element.removeAttribute('id');
  });
  resetEntranceState(clone);
  return clone;
}

/**
 * @param {HTMLElement} sectionRoot
 */
function randomizeFromStash(sectionRoot) {
  const maxProducts = Number(sectionRoot.dataset.maxProducts);
  const stash = sectionRoot.querySelector('[data-randomize-stash]');

  if (!stash || !maxProducts) {
    sectionRoot.classList.add('featured-collection-randomizer--ready');
    return;
  }

  const stashMap = buildStashMap(stash);
  let handles = readEmbeddedHandles(sectionRoot).filter((handle) => stashMap.has(handle));

  if (!handles.length) {
    handles = [...stashMap.keys()];
  }

  if (!handles.length) {
    sectionRoot.classList.add('featured-collection-randomizer--ready');
    return;
  }

  const selectedHandles = shuffle(handles).slice(0, maxProducts);
  const lists = sectionRoot.querySelectorAll('.resource-list');

  lists.forEach((list) => {
    const slots = [...list.querySelectorAll('[data-randomize-slot], .resource-list__item')];

    slots.forEach((slot, index) => {
      const handle = selectedHandles[index];
      const source = handle ? stashMap.get(handle) : undefined;
      if (!source) return;

      slot.replaceWith(cloneStashItem(source));
    });
  });

  sectionRoot.classList.add('featured-collection-randomizer--ready');

  sectionRoot.querySelectorAll('.resource-list').forEach((list) => {
    if (list instanceof HTMLElement) {
      delete list.dataset.productCardEntranceBound;
      delete list.dataset.entranceRevealed;
    }
  });
  delete sectionRoot.dataset.productCardEntranceBound;
  delete sectionRoot.dataset.entranceRevealed;

  requestAnimationFrame(() => {
    document.dispatchEvent(new CustomEvent('product-card:entrances-refresh'));
  });
}

function initFeaturedCollectionRandomizers() {
  document.querySelectorAll('[data-featured-collection-randomizer]').forEach((sectionRoot) => {
    if (!(sectionRoot instanceof HTMLElement)) return;
    randomizeFromStash(sectionRoot);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFeaturedCollectionRandomizers);
} else {
  initFeaturedCollectionRandomizers();
}
